import PropTypes from 'prop-types';
import React from 'react';
import {
  StyledCard,
  CardHeader,
  CardAccent,
  CardTitle,
} from './StyledComponents';

/**
 * Card Component - A themed card container with optional title and accent styles
 *
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Content to display inside the card
 * @param {ReactNode} props.title - Optional title for the card (can be text or JSX with icons)
 * @param {string} props.titleSize - Size of the title: 'small', 'normal', 'large'
 * @param {boolean} props.accent - Adds orange left border to the entire card
 * @param {boolean} props.accentTitle - Adds orange accent bar next to the title
 * @param {boolean} props.headerBorder - Adds border under the header
 * @param {boolean} props.noPadding - Removes default padding from the card
 * @param {boolean} props.compact - Uses compact padding (16px instead of 20px)
 * @param {boolean} props.fullHeight - Makes the card fill its parent height
 * @param {boolean} props.transparent - Removes background, border, and shadow
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props...rest - Any other props to pass to the card container
 */
const Card = ({
  children,
  title,
  titleSize = 'normal',
  accent = false,
  accentTitle = false,
  headerBorder = false,
  className,
  noPadding = false,
  compact = false,
  fullHeight = false,
  transparent = false,
  ...props
}) => {
  return (
    <StyledCard
      className={className}
      noPadding={noPadding}
      accent={accent}
      compact={compact}
      fullHeight={fullHeight}
      transparent={transparent}
      {...props}
    >
      {title && (
        <CardHeader withBorder={headerBorder} noMargin={!children}>
          {accentTitle && <CardAccent />}
          <CardTitle size={titleSize}>{title}</CardTitle>
        </CardHeader>
      )}
      {children}
    </StyledCard>
  );
};

Card.propTypes = {
  children: PropTypes.node,
  title: PropTypes.node, // Changed from string to node to support JSX/icons
  titleSize: PropTypes.oneOf(['small', 'normal', 'large']),
  accent: PropTypes.bool,
  accentTitle: PropTypes.bool,
  headerBorder: PropTypes.bool,
  className: PropTypes.string,
  noPadding: PropTypes.bool,
  compact: PropTypes.bool,
  fullHeight: PropTypes.bool,
  transparent: PropTypes.bool,
};

export default Card;

// Also export styled components for extension
export { StyledCard, CardHeader, CardAccent, CardTitle };
